import filterQueryGenerator from 'dashboard/helper/filterQueryGenerator';

// Presentation statuses returned by the API (`status`): draft, scheduled, processing, paused, completed, cancelled, failed
export const CAMPAIGN_STATUSES = [
  'draft',
  'scheduled',
  'processing',
  'paused',
  'completed',
  'cancelled',
  'failed',
];

export const STATUS_COLOR = {
  draft: 'text-n-slate-11 bg-n-alpha-2',
  scheduled: 'text-n-blue-11 bg-n-blue-3',
  processing: 'text-n-amber-11 bg-n-amber-3',
  paused: 'text-n-amber-11 bg-n-amber-3',
  completed: 'text-n-teal-11 bg-n-teal-3',
  cancelled: 'text-n-slate-10 bg-n-alpha-2',
  failed: 'text-n-ruby-11 bg-n-ruby-3',
};

export const RECIPIENT_STATUS_COLOR = {
  queued: 'text-n-slate-11',
  sending: 'text-n-blue-11',
  sent: 'text-n-blue-11',
  delivered: 'text-n-teal-11',
  read: 'text-n-teal-11',
  failed: 'text-n-ruby-11',
  skipped: 'text-n-slate-10',
  cancelled: 'text-n-slate-10',
};

export const RECIPIENT_FILTERS = [
  'all',
  'pending',
  'sent',
  'delivered',
  'read',
  'failed',
  'skipped',
  'cancelled',
];

export const ERROR_CODES = [
  'missing_phone',
  'invalid_phone',
  'blacklisted',
  'duplicate',
  'missing_contact_inbox',
  'template_error',
  'provider_error',
  'invalid_recipient',
  'cancelled',
  'outside_messaging_window',
  'rate_limited',
  'timeout',
  'provider_unavailable',
  'internal_error',
  'interrupted',
];

export const RUNNING_STATUSES = ['processing', 'paused'];

export const isRunning = status => RUNNING_STATUSES.includes(status);

// Confirmation threshold for "you are about to send to N contacts" prompts
export const LARGE_AUDIENCE = 1000;

export const formatDuration = (minutes, t) => {
  const m = Math.max(0, Math.round(minutes || 0));
  if (m < 1) return t('WASAPI_CAMPAIGNS.DURATION.UNDER_MINUTE');
  if (m < 60) return t('WASAPI_CAMPAIGNS.DURATION.MINUTES', { count: m });
  const h = Math.floor(m / 60);
  const rest = m % 60;
  if (h < 48) {
    return rest
      ? t('WASAPI_CAMPAIGNS.DURATION.HOURS_MINUTES', {
          hours: h,
          minutes: rest,
        })
      : t('WASAPI_CAMPAIGNS.DURATION.HOURS', { count: h });
  }
  return t('WASAPI_CAMPAIGNS.DURATION.DAYS', { count: Math.round(h / 24) });
};

export const formatDateTime = (value, locale = 'en', timeZone = null) => {
  if (!value) return '—';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat((locale || 'en').replace('_', '-'), {
    dateStyle: 'short',
    timeStyle: 'short',
    ...(timeZone ? { timeZone } : {}),
  }).format(date);
};

export const formatNumber = (value, locale = 'en') =>
  new Intl.NumberFormat((locale || 'en').replace('_', '-')).format(value || 0);

export const formatPercent = (value, locale = 'en') => {
  if (value === null || value === undefined) return null;
  return `${new Intl.NumberFormat((locale || 'en').replace('_', '-'), {
    maximumFractionDigits: 1,
  }).format(value)}%`;
};

// datetime-local <input> value (local time, no seconds) ↔ ISO string
export const toLocalInputValue = value => {
  if (!value) return '';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
};

export const fromLocalInputValue = value =>
  value ? new Date(value).toISOString() : null;

// Advanced filter rows (camelCase, as edited by ConditionRow) → API segment { payload: [...] }
export const buildSegment = filters => {
  const usable = (filters || []).filter(
    f => f.attributeKey && f.filterOperator
  );
  if (!usable.length) return null;
  const snake = usable.map(f => ({
    attribute_key: f.attributeKey,
    filter_operator: f.filterOperator,
    values: f.values,
    query_operator: f.queryOperator || 'and',
    custom_attribute_type: f.customAttributeType,
    attribute_model: f.attributeModel,
  }));
  return filterQueryGenerator(snake);
};

// API segment payload → ConditionRow models (values back to the shape the inputs expect)
export const segmentToFilters = segment => {
  const payload = segment?.payload || [];
  return payload.map(item => ({
    attributeKey: item.attribute_key,
    filterOperator: item.filter_operator,
    values: Array.isArray(item.values) ? item.values : [item.values],
    queryOperator: item.query_operator || 'and',
    attributeModel: item.attribute_model || 'standard',
    customAttributeType: item.custom_attribute_type,
  }));
};

// Summary line of a campaign audience ("2 labels + 1 filter")
export const audienceSummary = (audience, segment, t) => {
  const labels = (audience || []).filter(a => a.type === 'Label').length;
  const filters = (segment?.payload || []).length;
  const parts = [];
  if (labels)
    parts.push(t('WASAPI_CAMPAIGNS.AUDIENCE.LABELS_COUNT', { count: labels }));
  if (filters)
    parts.push(
      t('WASAPI_CAMPAIGNS.AUDIENCE.FILTERS_COUNT', { count: filters })
    );
  return parts.length ? parts.join(' + ') : t('WASAPI_CAMPAIGNS.AUDIENCE.NONE');
};
